import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import {OrderError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import type {CreateOrderRequest, OrderServiceInterface} from '@/src/ports/services/order-service'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import {CreateOrderProperty} from '@/src/entities/order'

type AddressInput = {
  street: string
  house: string
  lobby?: string
  housing?: string
  building?: string
  floor?: string
  flat?: string
  zip?: string
}

export type CreateOrderInput = Omit<CreateOrderRequest, 'properties'> & {
  properties?: Record<string, unknown> | AddressInput
  recipient?: {
    properties: Record<string, unknown> | AddressInput
  }
  company?: {
    properties: Record<string, unknown> | AddressInput
  }
}

/**
 * Create Order Use Case
 *
 * Orchestrates order creation with proper data transformation.
 * Moves business logic from OrderScreen component to application layer.
 *
 * Responsibilities:
 * - Transform address data into formatted string
 * - Convert properties object to API-expected array format
 * - Delegate to OrderService for actual creation
 * - Handle and report errors
 *
 * @example
 * const result = await createOrderUseCase.execute(orderData, locations)
 * if (result.isOk()) {
 *   router.push(Routes.order.successPage.replace('<order_id>', result.value))
 * }
 */
@injectable()
export class CreateOrderUseCase {
  @inject(DI.IOrderService)
  orderService!: OrderServiceInterface

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  /**
   * Format address from structured input to string
   */
  private formatAddress(address: AddressInput, zip?: string): string {
    const parts = [address.street, address.house]

    if (address.lobby) {
      parts.push(`п. ${address.lobby}`)
    }

    if (address.housing) {
      parts.push(`к. ${address.housing}`)
    }

    if (address.building) {
      parts.push(`ст. ${address.building}`)
    }

    if (address.floor) {
      parts.push(`эт. ${address.floor}`)
    }

    if (address.flat) {
      parts.push(`кв. ${address.flat}`)
    }

    const addressStr = parts.join(', ')
    return zip ? `${zip}, ${addressStr}` : addressStr
  }

  /**
   * Transform properties object to API format
   */
  private transformProperties(
    properties: (Record<string, unknown> | AddressInput) | undefined,
  ): OperationResult<CreateOrderProperty[], OrderError> {
    if (!properties) {
      return Result.ok([])
    }

    const result: CreateOrderProperty[] = []
    const propertiesCopy = {...properties}

    // Handle address transformation
    if ('address' in propertiesCopy && propertiesCopy.address) {
      const address = propertiesCopy.address as AddressInput
      const zip = 'zip' in propertiesCopy ? String(propertiesCopy.zip || '') : undefined

      propertiesCopy.address = this.formatAddress(address, zip)
      propertiesCopy.flat = address.flat
      propertiesCopy.floor = address.floor
      propertiesCopy.house = address.house
      propertiesCopy.lobby = address.lobby
      propertiesCopy.housing = address.housing
      propertiesCopy.building = address.building
      propertiesCopy.street = address.street
      propertiesCopy.zip = zip
    }

    for (const code of Object.keys(propertiesCopy) as (keyof typeof propertiesCopy)[]) {
      const value = propertiesCopy[code]
      if (value != null) {
        result.push({code, value})
      }
    }

    return Result.ok(result)
  }

  async execute(data: CreateOrderInput): Promise<OperationResult<string, OrderError>> {
    const propertiesResult = this.transformProperties({
      ...data.recipient?.properties,
      ...data.company?.properties,
      ...data.properties,
    })

    if (propertiesResult.isFail()) {
      return Result.fail(propertiesResult.error)
    }

    const request: CreateOrderRequest = {
      payment_service_id: data.payment_service_id,
      delivery_service_id: data.delivery_service_id,
      person_type: data.person_type,
      location_id: data.location_id,
      custom_location: data.custom_location,
      pickup_point_id: data.pickup_point_id,
      bonus_amount: data.bonus_amount,
      promo_code: data.promo_code,
      properties: propertiesResult.value,
      agree: data.agree,
      comment: data.comment,
    }

    // OrderService now returns Result, so we delegate error handling to it
    return new Promise((resolve, reject) => {
      this.orderService.create(request).then(result => {
        if (result.isOk()) {
          resolve(result)
        } else {
          reject(result.mapError(err => err))
        }
      })
    })
  }
}
